import React, { useState, useMemo } from "react";
import {
  IconSearch,
  IconFilter,
  IconArrowDownLeft,
  IconFlame,
  IconCheck,
  IconRotate,
  IconBox,
  IconUsers,
  IconAlertCircle,
  IconCalculator,
} from "@tabler/icons-react";
import { iconStroke } from "../../config/config";

export default function CustodyRegisterView({
  register,
  isLoading,
  onActionReturn,
  onActionConsume,
  onActionReconcile,
  onOpenUnifiedSettle,
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [deptFilter, setDeptFilter] = useState("all");

  // Filtered rows
  const filteredRows = useMemo(() => {
    return (register || []).filter((r) => {
      const remaining = parseFloat(r.remaining_base_quantity) || 0;
      const isClosed =
        r.status === "closed" ||
        r.status === "returned" ||
        r.status === "reconciled" ||
        remaining <= 0.0001;

      // Status filter
      if (statusFilter === "active") {
        if (isClosed) return false;
      } else if (statusFilter === "closed") {
        if (!isClosed) return false;
      } else if (statusFilter === "partially_returned") {
        if (r.status !== "partially_returned" || remaining <= 0.0001) return false;
      } else if (statusFilter !== "all" && r.status !== statusFilter) {
        return false;
      }

      // Department filter
      if (deptFilter !== "all" && r.department !== deptFilter) return false;

      // Search
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const emp = (r.employee_name || r.user_id || "").toLowerCase();
        const prod = (r.item_title || "").toLowerCase();
        const issNo = (r.issue_no || "").toLowerCase();
        if (!emp.includes(q) && !prod.includes(q) && !issNo.includes(q)) return false;
      }
      return true;
    });
  }, [register, searchQuery, statusFilter, deptFilter]);

  // Unique departments for filter
  const departments = useMemo(() => {
    const set = new Set();
    (register || []).forEach((r) => {
      if (r.department) set.add(r.department);
    });
    return Array.from(set);
  }, [register]);

  return (
    <div className="space-y-4">
      {/* Filters & Search Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-base-100 p-4 rounded-2xl border border-base-200 shadow-sm">
        <div className="flex flex-wrap items-center gap-3 flex-1 min-w-[280px]">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <IconSearch
              className="absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40"
              size={18}
              stroke={iconStroke}
            />
            <input
              type="text"
              placeholder="Search by custodian, product, or issue #..."
              className="input input-sm input-bordered w-full pl-9 text-xs"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Status Filter */}
          <select
            className="select select-sm select-bordered text-xs"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="active">Active Custody Only</option>
            <option value="partially_returned">Partially Returned</option>
            <option value="closed">Closed / Fully Reconciled</option>
            <option value="all">All Records</option>
          </select>

          {/* Department Filter */}
          {departments.length > 0 && (
            <select
              className="select select-sm select-bordered text-xs"
              value={deptFilter}
              onChange={(e) => setDeptFilter(e.target.value)}
            >
              <option value="all">All Departments</option>
              {departments.map((d, i) => (
                <option key={i} value={d}>
                  {d}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="text-xs text-base-content/60 font-medium">
          Showing {filteredRows.length} of {register?.length || 0} custody records
        </div>
      </div>

      {/* Table */}
      <div className="bg-base-100 rounded-2xl border border-base-200 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center p-12 text-gray-400">
            <IconRotate className="animate-spin mb-3" size={32} stroke={iconStroke} />
            <p className="text-xs">Loading custody register...</p>
          </div>
        ) : filteredRows.length === 0 ? (
          <div className="text-center py-12 px-4">
            <IconBox className="mx-auto text-base-content/30 mb-2" size={40} stroke={iconStroke} />
            <h4 className="font-bold text-base-content text-sm">No custody records found</h4>
            <p className="text-xs text-base-content/60 max-w-sm mx-auto mt-1">
              No staff members currently have matching outstanding stock under custody.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table table-sm w-full">
              <thead>
                <tr className="bg-base-200/50 text-base-content/70 text-xs">
                  <th>Issue Details</th>
                  <th>Custodian</th>
                  <th>Product</th>
                  <th className="text-right">Issued</th>
                  <th className="text-right">Returned</th>
                  <th className="text-right">POS Sold</th>
                  <th className="text-right">Usage/Loss</th>
                  <th className="text-right font-bold text-primary">Custody Rem.</th>
                  <th className="text-right text-warning">Pending Sales</th>
                  <th className="text-right text-success font-bold">Returnable</th>
                  <th>Status</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-base-200 text-xs">
                {filteredRows.map((row) => {
                  const remaining = parseFloat(row.remaining_base_quantity) || 0;
                  const pending = parseFloat(row.pending_sales_base_quantity) || 0;
                  const returnable = parseFloat(row.expected_returnable_base_quantity) || 0;

                  return (
                    <tr key={row.id} className="hover:bg-base-200/40">
                      <td>
                        <div className="font-bold text-xs">{row.issue_no}</div>
                        <div className="text-[10px] text-base-content/50">
                          {new Date(row.issue_date || row.created_at).toLocaleDateString()}
                        </div>
                      </td>
                      <td>
                        <div className="font-semibold text-xs">
                          {row.employee_name || row.user_id}
                        </div>
                        <div className="text-[10px] text-base-content/50">
                          {row.department || "Staff"}
                        </div>
                      </td>
                      <td>
                        <div className="font-medium text-xs text-base-content">
                          {row.item_title}
                        </div>
                        <div className="text-[10px] text-base-content/50">
                          Base: {row.unit}
                        </div>
                      </td>
                      <td className="text-right font-mono">
                        <div>
                          {row.quantity} {row.unit_name}
                        </div>
                        <div className="text-[10px] text-base-content/50">
                          ({row.base_quantity} {row.unit})
                        </div>
                      </td>
                      <td className="text-right font-mono text-success">
                        {row.returned_base_quantity > 0
                          ? `${row.returned_base_quantity} ${row.unit}`
                          : "-"}
                      </td>
                      <td className="text-right font-mono text-info">
                        {row.settled_base_quantity > 0
                          ? `${row.settled_base_quantity} ${row.unit}`
                          : "-"}
                      </td>
                      <td className="text-right font-mono text-warning">
                        {row.consumed_base_quantity > 0
                          ? `${row.consumed_base_quantity} ${row.unit}`
                          : "-"}
                      </td>
                      <td className="text-right font-mono font-bold text-primary">
                        {remaining.toLocaleString()} {row.unit}
                      </td>
                      <td className="text-right font-mono text-warning">
                        {pending > 0 ? `${pending.toLocaleString()} ${row.unit}` : "0"}
                      </td>
                      <td className="text-right font-mono font-bold text-success">
                        {returnable.toLocaleString()} {row.unit}
                      </td>
                      <td>
                        <span
                          className={`badge badge-xs font-semibold ${
                            row.status === "active"
                              ? "badge-primary"
                              : row.status === "partially_returned"
                              ? "badge-warning"
                              : "badge-neutral"
                          }`}
                        >
                          {row.status}
                        </span>
                      </td>
                      <td>
                        <div className="flex items-center justify-center gap-1.5">
                          <button
                            title="Audit, Return & Reconcile (Unified Dialogue)"
                            disabled={remaining <= 0}
                            onClick={() => (onOpenUnifiedSettle ? onOpenUnifiedSettle(row) : onActionReconcile(row))}
                            className="btn btn-primary btn-xs gap-1 font-medium shadow-sm"
                          >
                            <IconCalculator size={14} stroke={iconStroke} />
                            Audit & Settle
                          </button>
                          <button
                            title="Return to Main Stock"
                            disabled={remaining <= 0}
                            onClick={() => (onOpenUnifiedSettle ? onOpenUnifiedSettle(row) : onActionReturn(row))}
                            className="btn btn-ghost btn-xs text-success hover:bg-success/10"
                          >
                            <IconArrowDownLeft size={16} stroke={iconStroke} />
                          </button>
                          <button
                            title="Non-Sale Usage / Wastage"
                            disabled={remaining <= 0}
                            onClick={() => (onOpenUnifiedSettle ? onOpenUnifiedSettle(row) : onActionConsume(row))}
                            className="btn btn-ghost btn-xs text-warning hover:bg-warning/10"
                          >
                            <IconFlame size={16} stroke={iconStroke} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
